/**
 * Weekly Digest Page
 * Shows firm-wide weekly summary.
 */

import { useQuery } from '@tanstack/react-query';
import { Loader2, FileText, MessageSquare, Users, Link2, TrendingUp, CheckCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import apiClient from '@/services/api/ma-client';

const fmtBytes = (b: number) => b >= 1048576 ? `${(b / 1048576).toFixed(0)} MB` : `${(b / 1024).toFixed(0)} KB`;

export default function DigestPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['weekly-digest'],
    queryFn: async () => { const r = await apiClient.get('/manager/digest'); return r.data.data; },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--v4-accent)]" />
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="px-5 py-6 max-w-lg mx-auto space-y-6">
      <div>
        <h2 className="text-xl font-medium">Weekly Digest</h2>
        <p className="text-sm text-muted-foreground">{data.period}</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-card border border-border rounded-xl p-4">
          <Users className="h-5 w-5 text-[var(--v4-accent)] mb-2" />
          <p className="text-2xl font-bold">{data.stats.totalClients}</p>
          <p className="text-sm text-muted-foreground">Total Clients</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <Link2 className="h-5 w-5 text-green-600 dark:text-green-400 mb-2" />
          <p className="text-2xl font-bold">{data.stats.xeroConnected}</p>
          <p className="text-sm text-muted-foreground">Accounting Connected</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <FileText className="h-5 w-5 text-amber-600 dark:text-amber-400 mb-2" />
          <p className="text-2xl font-bold">{data.stats.newDocuments}</p>
          <p className="text-sm text-muted-foreground">Docs This Week</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <MessageSquare className="h-5 w-5 text-blue-600 dark:text-blue-400 mb-2" />
          <p className="text-2xl font-bold">{data.stats.conversations}</p>
          <p className="text-sm text-muted-foreground">Conversations</p>
        </div>
      </div>

      {/* Highlights */}
      {data.highlights.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-medium">This Week</h3>
          {data.highlights.map((h: string, i: number) => (
            <div key={i} className="flex items-start gap-3 p-3 bg-muted/50 rounded-xl">
              <TrendingUp className="h-4 w-4 text-[var(--v4-accent)] shrink-0 mt-0.5" />
              <p className="text-sm text-foreground">{h}</p>
            </div>
          ))}
        </div>
      )}

      {/* Most Active Clients */}
      {data.activeClients?.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-medium">Most Active Clients</h3>
          {data.activeClients.map((c: any, i: number) => (
            <div key={i} className="flex items-center justify-between py-2 border-b border-border last:border-0">
              <span className="text-sm">{c.display_name}</span>
              <span className="text-xs text-muted-foreground">{c.message_count} messages</span>
            </div>
          ))}
        </div>
      )}

      {/* Actions Needed */}
      {data.actions.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-medium">Actions Needed</h3>
          {data.actions.map((a: string, i: number) => (
            <div key={i} className="flex items-start gap-3 p-3 bg-amber-500/5 border border-amber-500/10 rounded-xl">
              <CheckCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
              <p className="text-sm text-foreground">{a}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
